import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';
const STORAGE_KEY = 'ss_review_theme';

function storedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

export function initTheme(): void {
  document.documentElement.dataset.theme = storedTheme() ?? 'light';
}

export function getTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // The applied theme should still update when storage is unavailable.
  }
  document.documentElement.dataset.theme = theme;
}

export function useTheme(): readonly [Theme, () => void] {
  const [theme, setCurrentTheme] = useState<Theme>(() => storedTheme() ?? getTheme());
  const toggleTheme = useCallback(() => {
    const nextTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(nextTheme);
    setCurrentTheme(nextTheme);
  }, [theme]);

  return [theme, toggleTheme] as const;
}

export function useForceLight(): void {
  useEffect(() => {
    const previous = document.documentElement.dataset.theme;
    document.documentElement.dataset.theme = 'light';
    return () => {
      document.documentElement.dataset.theme = storedTheme() ?? previous ?? 'light';
    };
  }, []);
}
